// Pure interpretation of the GET /api/projects/:id `connections` object
// (server/lib/projectConnections.js). No DOM, no fetches, no legacy guesses.
// Shape: { code, location:{locationId,city,alias,address}, lease:{leaseId,ref},
//          slack:{channel,url}, documents:{name,url} }

import { parseSlackHttpsUrl, slackChannelId } from "../../../lib/projectSlackUrl.js";

const OPAQUE_SLACK_ID = /^[CG][A-Z0-9]+$/;
const SLACK_NAME = /^#?[a-z0-9][a-z0-9_-]{0,79}$/;
const UUIDISH = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i;

const str = (v) => (typeof v === "string" ? v.trim() : typeof v === "number" ? String(v) : "");
const looksLikeUrl = (v) => /^[a-z][a-z0-9+.-]*:/i.test(v) || /^www\./i.test(v);

function parseHttps(value) {
  const s = str(value);
  if (!s || /[\s\\]/.test(s)) return null;
  try {
    const u = new URL(s);
    return u.protocol === "https:" && !u.username && !u.password && !u.port ? u : null;
  } catch { return null; }
}

/** Safe Slack target: legacy ID deep link or a supported Slack HTTPS URL. */
export function safeSlackUrl(value) {
  const s = str(value);
  if (!s) return null;
  const deep = /^slack:\/\/channel\?id=([CG][A-Z0-9]+)$/.exec(s);
  if (deep) return `slack://channel?id=${deep[1]}`;
  return parseSlackHttpsUrl(s)?.href ?? null;
}

/** Safe Drive target: drive.google.com folder only, canonicalised. */
export function safeDriveFolderUrl(value) {
  const u = parseHttps(value);
  if (!u || u.hostname !== "drive.google.com") return null;
  const m = /^\/drive\/(?:u\/\d+\/)?folders\/([A-Za-z0-9_-]+)\/?$/.exec(u.pathname);
  return m ? `https://drive.google.com/drive/folders/${m[1]}` : null;
}

/** Prefer a recorded name; channel URLs may supply a C/G ID, never invite tokens. */
export function slackLabel(channel, url) {
  const s = str(channel);
  if (s && !looksLikeUrl(s) && !OPAQUE_SLACK_ID.test(s) && SLACK_NAME.test(s)) {
    return s.startsWith("#") ? s : `#${s}`;
  }
  return slackChannelId(url) || slackChannelId(s) || "Slack channel";
}

export function locationHref(locationId) {
  return `/offices/${encodeURIComponent(locationId)}`;
}

// Direct AppShell routes (/offices/:locationId, /leases/:leaseId); plain anchors
// so the generic recordNavigationGuard still intercepts while dirty.
export function leaseHref(leaseId) {
  return `/leases/${encodeURIComponent(leaseId)}`;
}

/**
 * Pure: interpret connections into an ordered list of renderable entries.
 * Returns { code: string|null, entries: Entry[] } where Entry =
 *   { kind: "location"|"lease"|"slack"|"drive", label, detail, href, external }
 * Order: Location, Lease, Slack, Drive. Unresolved/invalid entries omitted.
 */
export function interpretConnections(connections) {
  const c = connections && typeof connections === "object" ? connections : {};
  const code = str(c.code) || null;
  const entries = [];

  const locId = str(c.location?.locationId);
  if (locId) {
    const alias = str(c.location.alias);
    const city = str(c.location.city);
    const label = [city, alias].filter((v, i, a) => v && a.indexOf(v) === i).join(" · ") || locId;
    entries.push({ kind: "location", title: "Location", label, detail: str(c.location.address) || null, href: locationHref(locId), external: false });

    const leaseId = str(c.lease?.leaseId);
    if (leaseId) {
      const ref = str(c.lease.ref);
      const readable = ref && !UUIDISH.test(ref) && !looksLikeUrl(ref) ? ref : null;
      entries.push({ kind: "lease", title: "Lease", label: readable || "Current lease", detail: null, href: leaseHref(leaseId), external: false });
    }
  }

  if (c.slack && (str(c.slack.channel) || str(c.slack.url))) {
    const href = safeSlackUrl(c.slack.url);
    // Name-only (no safe URL) stays visible as metadata, never clickable.
    const label = slackLabel(c.slack.channel, href);
    if (href || label !== "Slack channel") {
      entries.push({ kind: "slack", title: "Slack", label, detail: href ? null : "Name only · no link recorded", href, external: Boolean(href) });
    }
  }

  const driveHref = safeDriveFolderUrl(c.documents?.url);
  if (driveHref) {
    const name = str(c.documents.name);
    const label = name && !looksLikeUrl(name) ? name : "Google Drive folder";
    entries.push({ kind: "drive", title: "Documents", label, detail: null, href: driveHref, external: true });
  }

  return { code, entries };
}

export function hasConnections(connections) {
  const { code, entries } = interpretConnections(connections);
  return Boolean(code) || entries.length > 0;
}
